/**
 * Material-UI styles for the EmployeeList component.
 *
 * @module components/admin/EmployeeList
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */

/**
 * Returns a Material-UI styles object for the EmployeeList component.
 *
 * @param {Object} theme - The Material-UI theme object.
 * @returns {Object} A styles object keyed by class name.
 */
export const EmployeeList = (theme) => ({
  root: {
    width: '100%',
  },
  thead: {
    backgroundColor: '#e1e1e1',
    '& .MuiTableCell-head': {
      paddingTop: 9,
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(2),
    },
  },
  tbody: {
    '& .MuiTableCell-body': {
      paddingLeft: 12,
    },
  },
  cancelButton: {
    color: '#313387',
  },
  heading: {
    fontWeight: [600],
    fontSize: '0.9em',
    width: 250,
  },
  permissions: {
    fontWeight: [600],
    fontSize: '0.9em',
    width: 110,
  },
  role: {
    fontWeight: [600],
    fontSize: '0.9em',
    width: 200,
  },
  bold: {
    fontWeight: 'bold',
    fontSize: '0.9em',
  },
  built: {
    fontSize: '0.9em',
    display: 'flex',
    justifyContent: 'left',
  },
  permissionsTableCell: {
    whiteSpace: 'nowrap',
    color: '#b2335d',
    textDecoration: 'underline',
    textAlign: 'center',
    fontWeight: [600],
    fontSize: '0.9em',
  },
  firstNameCell: {
    paddingTop: theme.spacing(1),
  },
  lastNameCell: {
    fontSize: '0.9em',
  },
  emailIdCell: {
    fontSize: '0.9em',
    height: 76,
  },
  roleCell: {
    fontSize: '0.9em',
  },
  groupCell: {
    textDecoration: 'underline',
    fontSize: '0.9em',
  },
  statusCell: {
    fontSize: '0.9em',
  },
  addIcon: {
    marginLeft: '0em',
    minWidth: '0vw',
    backgroundColor: '#882647',
    color: '#fff',
    width: '1.3vw',
    height: '1.1vw',
    borderRadius: 2,
  },
  titleCell: {
    fontSize: '0.9em',
  },
  phoneNumberCell: {
    fontSize: '0.9em',
  },
  small: {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  buttonSqr: {
    padding: '5px',
    marginRight: '5px',
    minHeight: 0,
    minWidth: 0,
  },
  buttonEditSqr: {
    padding: 5,
    marginRight: 5,
    minHeight: 0,
    minWidth: 0,
  },
  btnDisableSqr: {
    padding: '5px',
    marginRight: '5px',
    minHeight: 0,
    minWidth: 0,
    pointerEvents: 'none',
    opacity: '0.5',
  },
  btnEditDisableSqr: {
    padding: 5,
    marginRight: 3,
    minHeight: 0,
    minWidth: 0,
    pointerEvents: 'none',
    opacity: '0.5',
  },
  tableRow: {
    cursor: 'pointer',
  },
  table: {
    tableLayout: 'fixed',
    width: ' 100%',
  },
  hard_left: {
    position: 'absolute',
    left: 120,
    width: 155,
    paddingTop: 20,
    border: 'none',
  },
  hard_left_action_tbody: {
    position: 'absolute',
    left: 0,
    width: 120,
    paddingTop: 24,
    border: 'none',
  },
  even_left_action: {
    position: 'absolute',
    left: 0,
    width: 120,
    paddingTop: 20,
    height: 77,
    backgroundColor: '#f5f5f5',
  },
  even_left: {
    position: 'absolute',
    left: 120,
    width: 155,
    paddingTop: 20,
    height: 77,
    backgroundColor: '#f5f5f5',
  },
  next_left: {
    position: 'absolute',
    '*position': 'relative',
    left: 275,
    width: 110,
    paddingTop: 28,
    fontSize: 12,
    border: 'none',
  },
  even_next_left: {
    position: 'absolute',
    fontSize: 12,
    left: 275,
    width: 110,
    paddingTop: 28,
    height: 77,
    backgroundColor: '#f5f5f5',
  },
  hard_left_head: {
    position: 'absolute',
    ' *position': 'relative', /* ie7 */
    left: 115,
    width: 155,
    background: '#e1e1e1',
    fontSize: 12,
    fontWeight: 'bold',
  },
  hard_left_action: {
    position: 'absolute',
    ' *position': 'relative', /* ie7 */
    left: 0,
    background: '#e1e1e1',
    fontSize: 12,
    width: 115,
    fontWeight: 'bold',
  },
  next_left_head: {
    position: 'absolute',
    '*position': 'relative',
    left: 270,
    width: 130,
    background: '#e1e1e1',
    fontSize: 12,
    fontWeight: 'bold',
  },
  outer: { position: 'relative' },
  inner: {
    overflowX: 'scroll',
    overflowY: 'visible',
    marginLeft: 385,
    [theme.breakpoints.between(1280, 1500)]: {
      width: 820,
    },
    [theme.breakpoints.down('md')]: {
      width: 1200,
    },
    [theme.breakpoints.down('sm')]: {
      width: 1270,
    },
    [theme.breakpoints.between(1500, 1607.95)]: {
      width: 970,
    },
    [theme.breakpoints.between(1600, 1708)]: {
      width: 1128,
    },
  },
  noWrap: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  actions: {
    width: 100,
    fontWeight: [600],
    fontSize: '0.9em',
  },
  noData: {
    position: 'absolute',
    top: 100,
    width: '100%',
    textAlign: 'center',
  },
  noAssign: {
    marginBottom: 0,
  },
  noAssignPara: {
    fontSize: 12,
  },
  addButton: {
    color: 'white',
    minWidth: '10%',
    height: 36,
    fontSize: 12,
    whiteSpace: 'nowrap',
    paddingRight: 12,
    backgroundColor: '#284375',
    margin: '0px 5px',
    '&:hover': {
      backgroundColor: '#647087',
    },
  },
  plus: {
    fontSize: 16,
    fontWeight: 600,
    paddingRight: 5,
  },
  dialog: {
    '& .MuiDialog-paperWidthSm': {
      width: '31%',
    },
  },
  Deletedialog: {
    '& .MuiDialog-paperWidthSm': {
      width: '20%',
    },
  },
  assignRole: {
    color: '#882647',
    cursor: 'pointer',
  },
  deleteEmployee: {
    color: 'red',
    cursor: 'pointer',
  },
  centerLabel: {
    marginTop: 8,
  },
  tempRole: {
    fontWeight: [600],
    fontSize: '0.9em',
    width: 150,
    textAlign: 'center',
  },
  tempRoleSpan: {
    borderBottom: '1px solid #000',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  arrowIcon: {
    '& .MuiNativeSelect-iconOutlined': {
      right: '14px !important',
    },
  },
  delete: {
    color: '#888484',
    cursor: 'pointer',
  },
  text: {
    color: '#000000b5',
  },
  errorAlert: {
    backgroundColor: '#600727',
    marginBottom: 10,
  },
  btnLogin: {
    display: 'none',
  },
});

export default EmployeeList;
